import React, { useEffect, useRef, useState } from "react";
import axios from 'axios';
import { toast, ToastContainer } from "react-toastify";
import useHandleErrors from "../../utils/user.utils";
import { useUser } from '../../utils/userContext';
import moment from 'moment';


const Holidays = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState([]);
    const handleErrors = useHandleErrors();
    const token = localStorage.getItem("token");
    const { user } = useUser();
    const hasFetchedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [filteredData, setFilteredData] = useState([]);


    // Handle File Selection
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Download Excel Template API
    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BE_URL}/holidayTemplate`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file download
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Holidays_Template.xlsx'); // Set file name
            document.body.appendChild(link);
            link.click();
            toast.success("Template downloaded successfully!", {
                position: toast.POSITION.TOP_CENTER,
            });
        } catch (error) {
            handleErrors(error.response);
        }
    };

    // Bulk Upload API
    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error("Please select a file to upload.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("UpdatedBy", user?.EmployeeId);

        setUploading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BE_URL}/holidaysBulkUpload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200) {
                toast.success(response.data?.message, {
                    position: toast.POSITION.TOP_CENTER,
                });
                fetchFiles(); // Refresh table data
                setSelectedFile(null);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error.response);
        } finally {
            setUploading(false);
        }
    };

    // Fetch Uploaded Files
    const fetchFiles = async () => {
        setUploading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BE_URL}/getHolidays`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200) {
                setFiles(response.data || []);
                setFilteredData(response.data || [])
            } else {
                handleErrors(response);
            }
        } catch (error) {
            setFiles([]); // Show "No Data" if error occurs
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchFiles();
            hasFetchedRef.current = true;
        }
    }, []);

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);


    const paginate = pageNumber => setCurrentPage(pageNumber);

    return (
        <div>
            {uploading && <div id="preloading"><span className="loader"></span></div>}
            <ToastContainer />
            {/* Uploaded Data Table */}
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Holidays</h3>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary" onClick={handleDownloadTemplate}>
                                <i className="ri-download-line"></i> Download Template
                            </button>
                        </div>
                    </div>

                    {/* Info Message */}
                    <div className="alert alert-info" role="alert">
                        <i className="ri-information-line"></i> Download the template, fill in the data, and upload the file.
                    </div>

                    {/* File Upload Section */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="col-lg-12 col-md-12">
                                <div className="form-group">
                                    <label htmlFor="Document">Document</label>
                                    <div className="d-flex align-items-center">
                                        <div className="custom-file mr-2">
                                            <input
                                                type="file"
                                                className="custom-file-input"
                                                id="Document"
                                                onChange={handleFileChange}
                                            />
                                            <label className="custom-file-label" htmlFor="Document">
                                                {selectedFile ? selectedFile.name : "Choose a file"}
                                            </label>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={handleUpload}
                                            disabled={!selectedFile}
                                        >
                                            <i className="ri-upload-cloud-2-line"></i> Upload
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {currentRecords?.length === 0 ? (
                        <div className="container">
                            <div className="row vh-50 justify-content-center align-items-center">
                                <div className="col-auto">
                                    <div className="no-data-wrapper">
                                        <div className="no-data-found">
                                            <i className="ri-file-damage-fill"></i>
                                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                            <p>No Holidays Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <h5 className="title mt-1 mb-0">Holiday List</h5>
                                            <div className="table-responsive leave-information mt-3">
                                                <table className="table grid">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>S.No.</th>
                                                            <th>Holiday Name</th>
                                                            <th>Day</th>
                                                            <th>Date</th>
                                                            <th>Type</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentRecords.map((holiday, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{holiday.holidayName}</td>
                                                                <td>{holiday.day}</td>
                                                                <td>{moment(holiday.date, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY")}</td>
                                                                <td>{holiday.type}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination */}
                                            <div className="row border-top pt-3">
                                                <div className="col-auto">
                                                    <p className="mt-1 mb-0">Records</p>
                                                </div>
                                                <div className="col-1 record-drop">
                                                    <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                        <option value="5">5</option>
                                                        <option value="10">10</option>
                                                        <option value="15">15</option>
                                                        <option value="20">20</option>
                                                        <option value="25">25</option>
                                                    </select>
                                                </div>
                                                <div className="col">
                                                    <nav>
                                                        <ul className="pagination justify-content-center">
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button className="page-link" onClick={() => paginate(1)}>
                                                                    <i className="ri-arrow-left-double-line"></i>
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                                                                    <i className="ri-arrow-left-s-line"></i>
                                                                </button>
                                                            </li>
                                                            {Array.from({ length: totalPages }, (_, index) => (
                                                                <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                    <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                        {index + 1}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                    <i className="ri-arrow-right-s-line"></i>
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                    <i className="ri-arrow-right-double-fill"></i>
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                                <div className="col-auto">
                                                    <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Holidays;
